import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api, fmtUSD, fmtCompact, fmtPct, pdi, pdiTier } from "../lib/api";
import { AGENTS } from "../lib/agents";
import { ALL_SECTIONS } from "../lib/sections";

import StrategyTab from "./tabs/StrategyTab";
import OpportunitiesTab from "./tabs/OpportunitiesTab";
import SentinelTab from "./tabs/SentinelTab";
import ScorecardView from "./ScorecardView";
import StrategyMultiplierView from "./StrategyMultiplierView";
import RetrospectivesView from "./RetrospectivesView";
import MethodsLabView from "./MethodsLabView";
import MarketOperatingPictureView from "./MarketOperatingPictureView";
import TeamManagementView from "./TeamManagementView";
import DecisionQueueView from "./DecisionQueueView";
import NegotiationsInFlightView from "./NegotiationsInFlightView";
import {
  CheckCircle, Warning, XCircle, ShieldCheckered, Robot, Toolbox,
  Brain, Sparkle, Wrench, Globe, Coins, TrendUp, Broadcast, HardDrives,
  CalendarBlank, ChartLineUp, Books, ListMagnifyingGlass, CheckSquare,
  Lightning, Calculator, ChartLine, ArrowRight, Target, X, PaperPlaneTilt,
  GlobeHemisphereWest, FileText, Database, Pulse, Crosshair,
  ChartBar,
} from "@phosphor-icons/react";

const TIER_ACCENT = { ORCHESTRATOR: "fuchsia", SME: "emerald", UTILITY: "sky", SYSTEM: "amber" };
const TIER_ICON = { ORCHESTRATOR: Brain, SME: Sparkle, UTILITY: Wrench, SYSTEM: Robot };

// ─── Section dispatch table ───────────────────────────────────────────────────
export function SectionView({ section, openProof, openSourcing, stageFocus, onNavigate }) {
  switch (section) {
    case "command":       return <StrategyTab openProof={openProof} openSourcing={openSourcing} onNavigate={onNavigate} />;
    case "opportunities": return <OpportunitiesTab openProof={openProof} stageFocus={stageFocus} />;
    case "today":         return <TodayView openSourcing={openSourcing} />;
    case "scorecard":     return <ScorecardView openProof={openProof} openSourcing={openSourcing} />;
    case "decisions":     return <DecisionQueueView />;
    case "negotiations":  return <NegotiationsInFlightView />;
    case "learning":      return <RetrospectivesView />;
    case "audit":         return <AuditTrailView />;
    case "approvals":     return <ApprovalsView />;
    case "agents":        return <AgentsView />;
    case "tools":         return <MethodsLabView />;
    case "simulator":     return <StrategyMultiplierView />;
    case "fx":
    case "commodities":
    case "signals":
    case "geopolitical":
    case "mop":          return <MarketOperatingPictureView />;
    case "collectors":    return <CollectorsView />;
    case "analyst":       return <SentinelTab openProof={openProof} />; // AI Analyst leans on Risk + LLM
    case "team":          return <TeamManagementView />;
    case "system":        return <SystemHealthView />;
    default:              return <div className="text-slate-500">Unknown section.</div>;
  }
}

// ─── Today · Maneuver Execution Feed ─────────────────────────────────────────
// Prioritized action queue of Doer-Agent plays. Each card is a single click
// → Sourcing Modal with the Strategic Partner Negotiation Playbook.
function TodayView({ openSourcing }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.ledger({ limit: 60 }).then(setRows).catch(() => setRows([]));
  }, []);

  if (!rows) return <Skeleton />;

  // Build prioritized maneuvers — interleave Should-Cost (largest gross delta)
  // with Rate Benchmark (highest risk-tier) so the queue spans both levers.
  const shouldCost = [...rows]
    .filter((r) => (r.delta || 0) > 0)
    .sort((a, b) => (b.delta || 0) - (a.delta || 0))
    .slice(0, 8)
    .map((r) => ({
      ...r,
      _mode: "SHOULD_COST",
      _title: "Should-Cost Maneuver",
      _icon: Calculator,
      _gap: r.delta,
      _gapLabel: "Value Gap vs Baseline",
      _why: "Realized award exceeded should-cost decomposition — Strategic Partner will rebuild the cost stack and surface the negotiable margin.",
    }));

  const rateBenchmark = [...rows]
    .filter((r) => (r.risk_score || 0) >= 55)
    .sort((a, b) => (b.realized || 0) - (a.realized || 0))
    .slice(0, 8)
    .map((r) => ({
      ...r,
      _mode: "RATE_BENCHMARK",
      _title: "Rate Parity Play",
      _icon: ChartLine,
      _gap: (r.realized || 0) * 0.08,                                  // assumed parity gap
      _gapLabel: "Rate Parity Gap (est.)",
      _why: "Counterparty risk is elevated — Strategic Partner will benchmark the rate card against peer awards and propose a parity ask.",
    }));

  // Interleave SC + RB so the feed alternates lever types
  const maneuvers = [];
  for (let i = 0; i < Math.max(shouldCost.length, rateBenchmark.length); i++) {
    if (shouldCost[i])    maneuvers.push(shouldCost[i]);
    if (rateBenchmark[i]) maneuvers.push(rateBenchmark[i]);
  }

  // Promote canonical Phase-3 demo scenarios to the top of the queue. We
  // patch the first qualifying Should-Cost and Rate-Benchmark cards with
  // Phase-3 narratives so the Today inbox always opens with the same
  // top-of-funnel storyline.
  const LEAD_NARRATIVES = {
    SHOULD_COST: "Awarded margin exceeds should-cost decomposition — Copper +3.2% MoM cannot justify the price; Strategic Partner will rebuild the cost stack.",
    RATE_BENCHMARK: "Vendor rate card is +8% above peer GSA CALC median — Strategic Partner will benchmark and propose a parity ask.",
  };
  const ordered = [];
  const used = new Set();
  for (const lever of ["SHOULD_COST", "RATE_BENCHMARK"]) {
    const idx = maneuvers.findIndex((m, i) => !used.has(i) && m._mode === lever);
    if (idx >= 0) {
      ordered.push({
        ...maneuvers[idx],
        _why: LEAD_NARRATIVES[lever],
        _amplification: true,
        _amplification_source: "OODA · Proactive Plays Agent",
      });
      used.add(idx);
    }
  }
  maneuvers.forEach((m, i) => { if (!used.has(i)) ordered.push(m); });
  const queue = ordered;

  const totalGap = queue.reduce((s, m) => s + (m._gap || 0), 0);

  return (
    <Wrapper
      title="Today · Maneuver Execution Feed"
      icon={Target}
      subtitle="Prioritized Strategic Partner plays — your operational inbox for executing the COP's strategic surface area."
    >
      <div
        data-testid="today-action-queue"
        className="bg-white border border-[#D1D5DB] rounded-sm"
      >
        <header className="px-5 py-3 border-b border-[#D1D5DB] bg-[#F3F4F6] flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display text-sm font-bold text-slate-950 tracking-tight flex items-center gap-2">
              <Lightning weight="duotone" size={14} className="text-amber-700" />
              ACTION QUEUE · {queue.length} MANEUVERS READY
            </div>
            <p className="text-xs text-slate-700 mt-0.5 font-mono font-medium">
              Each card launches the Strategic Partner Negotiation Playbook for the underlying purchase order.
            </p>
          </div>
          <div className="font-mono text-[11px] text-slate-700 tracking-mono-label font-bold">
            Aggregate Value Gap ·{" "}
            <span className="text-emerald-900 font-bold">{fmtCompact(totalGap)}</span>
          </div>
        </header>

        <ul
          className="divide-y divide-[#E5E7EB]"
          data-testid="today-maneuver-list"
        >
          {queue.length === 0 && (
            <li className="px-5 py-10 text-center text-slate-700 text-sm font-mono font-medium">
              No maneuvers queued. Pull a fresh ledger sync to repopulate the inbox.
            </li>
          )}
          {queue.map((m, i) => (
            <ManeuverCard
              key={`${m._mode}-${m.po_id}`}
              priority={i + 1}
              maneuver={m}
              onLaunch={() => openSourcing(m._mode, m)}
            />
          ))}
        </ul>
      </div>
    </Wrapper>
  );
}

function ManeuverCard({ priority, maneuver, onLaunch }) {
  const Icon = maneuver._icon;
  const isShouldCost = maneuver._mode === "SHOULD_COST";
  return (
    <li
      data-testid={`maneuver-card-${maneuver.po_id}`}
      className="px-5 py-4 grid grid-cols-12 gap-4 items-center hover:bg-[#F3F4F6]/40 transition-colors"
    >
      {/* PDI · leading confidence indicator — authoritative stamp */}
      <div className="col-span-12 md:col-span-2 flex items-center gap-3">
        <span className="font-mono text-[10px] text-slate-700 tracking-mono-label w-6 shrink-0 font-bold">
          {String(priority).padStart(2, "0")}
        </span>
        <div
          data-testid={`pdi-badge-${maneuver.po_id}`}
          title={`PDI ${pdi(maneuver.risk_score)} · ${pdiTier(maneuver.risk_score)} · Procurement Decision Intelligence`}
          className={`flex items-center justify-center w-16 h-12 rounded-md font-display text-2xl font-extrabold tabular-nums leading-none tier-${pdiTier(maneuver.risk_score)}`}
        >
          {pdi(maneuver.risk_score)}
        </div>
      </div>

      {/* lever title */}
      <div className="col-span-12 md:col-span-3 flex items-center gap-2">
        <Icon
          weight="duotone"
          size={18}
          className={isShouldCost ? "text-fuchsia-700" : "text-sky-700"}
        />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-display text-[13px] font-bold text-slate-950 truncate tracking-tight">
              {maneuver._title}
            </span>
            {maneuver._amplification && (
              <span
                data-testid={`amplification-badge-${maneuver.po_id}`}
                title={maneuver._amplification_source || "Amplification"}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm
                           bg-emerald-50 border border-emerald-700/40
                           font-mono text-[8.5px] font-bold tracking-mono-label text-emerald-900"
              >
                <Lightning weight="fill" size={9} /> AMPLIFICATION
              </span>
            )}
          </div>
          <div className="font-mono text-[10px] text-slate-700 tracking-mono-label font-bold">
            {isShouldCost ? "SHOULD-COST · STRATEGIC PARTNER" : "RATE BENCHMARK · STRATEGIC PARTNER"}
          </div>
        </div>
      </div>

      {/* subject — vendor + category */}
      <div className="col-span-12 md:col-span-3 min-w-0">
        <div className="text-[13px] text-slate-950 truncate font-medium" title={maneuver.supplier}>
          {maneuver.supplier}
        </div>
        <div className="font-mono text-[11px] text-slate-700 truncate font-medium">
          {maneuver.segment_name} · {maneuver.po_number}
        </div>
      </div>

      {/* value gap */}
      <div className="col-span-6 md:col-span-2 text-right">
        <div className="font-mono text-[10px] text-slate-700 tracking-mono-label font-bold">
          {maneuver._gapLabel}
        </div>
        <div className="font-mono text-lg text-emerald-900 font-bold leading-tight tabular-nums">
          {fmtUSD(maneuver._gap)}
        </div>
        <div className="font-mono text-[10px] text-slate-700 mt-0.5 truncate font-medium">
          realized {fmtCompact(maneuver.realized)}
        </div>
      </div>

      {/* launch */}
      <div className="col-span-6 md:col-span-2 flex justify-end">
        <button
          data-testid={`maneuver-launch-${maneuver.po_id}`}
          onClick={onLaunch}
          className="group inline-flex items-center gap-2 px-3 py-2 rounded-sm
                     border border-emerald-800/40 bg-emerald-50
                     font-mono text-[11px] tracking-mono-label text-emerald-900 font-bold
                     hover:bg-emerald-100 hover:border-emerald-700 transition-colors"
        >
          Launch Maneuver
          <ArrowRight weight="bold" size={12} className="group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>

      {/* Why · subtle strategic context line (full width) */}
      {maneuver._why && (
        <div className="col-span-12 -mt-1 pl-9 md:pl-12 border-l-2 border-emerald-700/30 ml-2 md:ml-0">
          <p className="text-[12px] text-slate-700 font-medium leading-snug pl-2.5">
            {maneuver._why}
          </p>
        </div>
      )}
    </li>
  );
}

// ─── Scorecard (OaaS) — moved to ./ScorecardView.jsx ─────────────────────────
// ─── (Retrospectives now rendered by RetrospectivesView · sidebar id "learning") ─

// ─── Audit Trail — Integrity assertion runs ──────────────────────────────────
function AuditTrailView() {
  const [a, setA] = useState(null);
  const [c, setC] = useState(null);
  useEffect(() => {
    api.integrity().then(setA).catch(() => setA({ assertions: [] }));
    api.soc2().then(setC).catch(() => setC({ scim_logs: [], gitleaks_history: [] }));
  }, []);
  if (!a || !c) return <Skeleton />;
  return (
    <Wrapper title="Audit Trail" icon={ListMagnifyingGlass} subtitle="11 SQL integrity assertions + SCIM/SSO events + gitleaks history feed.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-800">
        <div className="bg-slate-950 p-4">
          <div className="tracking-mono-label text-[10px] text-slate-500 mb-2">Integrity Assertions</div>
          <ul className="space-y-1.5">
            {a.assertions.map((x) => (
              <li key={x.id} className="flex items-baseline gap-2 text-xs">
                <span className="font-mono text-emerald-400 text-[10px]">[{x.id}]</span>
                <span className="text-slate-200 truncate flex-1">{x.name}</span>
                <span className={`font-mono text-[10px] ${x.status === "PASS" ? "text-emerald-300" : "text-rose-300"}`}>{x.status}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-slate-950 p-4">
          <div className="tracking-mono-label text-[10px] text-slate-500 mb-2">Recent SCIM / SSO Events</div>
          <ul className="space-y-1.5 text-xs">
            {c.scim_logs.map((s, i) => (
              <li key={i} className="flex items-baseline gap-2">
                <span className="font-mono text-emerald-400 text-[10px]">{new Date(s.ts).toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                <span className="text-slate-200 truncate">[{s.idp}] {s.action} → {s.target}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Wrapper>
  );
}

// ─── Approvals — DECIDE-stage opportunities awaiting action ──────────────────
function ApprovalsView() {
  const [data, setData] = useState(null);
  useEffect(() => { api.opportunitiesFunnel(200).then(setData).catch(() => setData({ opportunities: [] })); }, []);
  if (!data) return <Skeleton />;
  const decide = data.opportunities.filter((o) => o.ooda_stage === "DECIDE");
  return (
    <Wrapper title="Approvals Queue" icon={CheckSquare} subtitle="DECIDE-stage opportunities awaiting category-lead approval before realized value lock-in.">
      {decide.length === 0
        ? <Empty text="No items in DECIDE stage. Approvals queue is clear." />
        : <ul className="divide-y divide-slate-800">
            {decide.map((o) => (
              <li key={o.opportunity_id} className="px-5 py-3 flex items-center gap-3">
                <span className="px-1.5 py-0.5 rounded-sm bg-fuchsia-500/10 border border-fuchsia-500/40 text-fuchsia-300 text-[10px] tracking-mono-label font-mono">DECIDE</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-100 truncate">{o.title}</div>
                  <div className="font-mono text-[10px] text-slate-500">{o.po_number} · {o.supplier} · pipeline {fmtCompact(o.estimated_value)}</div>
                </div>
                <span className={`tier-${pdiTier(o.risk_score)} px-1.5 py-0.5 rounded-sm text-[10px] font-mono`} title="Procurement Decision Intelligence">PDI {pdi(o.risk_score)}</span>
              </li>
            ))}
          </ul>}
    </Wrapper>
  );
}

// ─── System of Agents · Command Console ──────────────────────────────────────
// Per-agent metadata: active pursuits, sensor feeds, signature decision style.
const AGENT_DOSSIER = {
  ORCHESTRATOR: {
    pursuits: ["DOD prime renegotiations", "category-lead executive briefings", "CFO insurance attestations"],
    sensors:  [{ name: "SEC EDGAR", icon: FileText }, { name: "FRED", icon: ChartBar }, { name: "Internal Ledger", icon: Database }],
    decisionStyle: "synthesizes multi-tier outputs into binding strategic verdicts",
  },
  SME: {
    pursuits: ["counterparty PDI scoring", "UNSPSC mapping confidence", "should-cost decomposition"],
    sensors:  [{ name: "SEC EDGAR", icon: FileText }, { name: "SAM.gov", icon: GlobeHemisphereWest }, { name: "GDELT", icon: Broadcast }],
    decisionStyle: "deep-domain reasoning over filings, exclusions, and unit-rate benchmarks",
  },
  UTILITY: {
    pursuits: ["vendor name normalization", "ISO-8601 timestamp hygiene", "FX coercion"],
    sensors:  [{ name: "USPS / DUNS", icon: Database }, { name: "USD Reference", icon: Coins }, { name: "Postal Geo", icon: Globe }],
    decisionStyle: "deterministic data-hygiene before downstream reasoning",
  },
  SYSTEM: {
    pursuits: ["100K-row SAP/Coupa imports", "SQL integrity assertions", "Mongo Context Ledger reconcile"],
    sensors:  [{ name: "PG Source of Truth", icon: Database }, { name: "Mongo Ledger", icon: Database }, { name: "Health Probes", icon: Pulse }],
    decisionStyle: "owns the wiring between source-of-truth Postgres and the Context Ledger",
  },
};

const PSEUDO_DECISIONS = [
  "Flagged Helsing Defense Tech · PDI 19 RED · FOCI review triggered",
  "Mapped 'Eternagen Solutions' → UNSPSC 4321 · confidence 0.88",
  "Should-Cost on PO-00482-DOD · 12% downside vs FAR baseline",
  "Rate benchmark · GSA CALC median below contract rate by 7%",
  "Concentration alert · Procellix at 41% of Cyber segment spend",
  "Integrity assertion A11 reconciled · 11/11 PASS",
  "Filing extracted · 10-K Item 1A flags going-concern paragraph",
  "Entity match · UEI K2Z9CDR7N5T6 → CAGE 8H9K1",
  "Normalized 'Helsing, Inc.' → 'Helsing Defense Tech'",
  "PDI re-score · supplier upgraded YELLOW → GREEN (PDI 72)",
  "Rate ceiling check · contract pricing within +1σ of CALC median",
  "Currency hygiene · EUR→USD coercion on 14 line items",
];

function AgentsView() {
  const [active, setActive] = useState(null); // active agent for drawer
  // local cache of per-agent confidence weight (1.0 = neutral)
  const [weights, setWeights] = useState(() =>
    AGENTS.reduce((acc, a) => ({ ...acc, [a.id]: 1.0 }), {})
  );
  const [directives, setDirectives] = useState({}); // textbox state per agent

  const byTier = useMemo(() => AGENTS.reduce(
    (acc, a) => ((acc[a.tier] = acc[a.tier] || []).push(a), acc), {}), []);

  const submitDirective = (agent) => {
    const text = (directives[agent.id] || "").trim();
    if (!text) {
      toast.error("Directive empty", { description: "Enter a tactical instruction before dispatching." });
      return;
    }
    toast.success("Command Received · Deploying Resources", {
      description: `${agent.name} acknowledged: "${text.slice(0, 80)}${text.length > 80 ? "…" : ""}"`,
      duration: 4500,
    });
    setDirectives((d) => ({ ...d, [agent.id]: "" }));
  };

  const updateWeight = (agentId, w) => {
    setWeights((prev) => ({ ...prev, [agentId]: w }));
  };

  return (
    <Wrapper
      title="System of Agents · Command Console"
      icon={Robot}
      subtitle="Tactical command surface for the 17-agent registry · click any card to open the agent dossier."
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#E5E7EB]" data-testid="agents-roster">
        {AGENTS.map((a) => (
          <AgentCard
            key={a.id}
            agent={a}
            weight={weights[a.id] ?? 1.0}
            directive={directives[a.id] || ""}
            onWeight={(w) => updateWeight(a.id, w)}
            onDirective={(text) => setDirectives((d) => ({ ...d, [a.id]: text }))}
            onSubmit={() => submitDirective(a)}
            onOpenDossier={() => setActive(a)}
          />
        ))}
      </div>

      <AgentDossierDrawer
        agent={active}
        weight={active ? (weights[active.id] ?? 1.0) : 1.0}
        onClose={() => setActive(null)}
      />
    </Wrapper>
  );
}

function AgentCard({ agent, weight, directive, onWeight, onDirective, onSubmit, onOpenDossier }) {
  const TierIcon = TIER_ICON[agent.tier] || Robot;
  const dossier = AGENT_DOSSIER[agent.tier] || AGENT_DOSSIER.SYSTEM;
  const weightLabel = weight < 0.7 ? "soft" : weight > 1.3 ? "aggressive" : "balanced";

  return (
    <div
      data-testid={`agent-card-${agent.id}`}
      className="bg-white p-4 lg:p-5 hover:bg-[#F3F4F6]/40 transition-colors"
    >
      <button
        type="button"
        onClick={onOpenDossier}
        data-testid={`agent-open-dossier-${agent.id}`}
        className="w-full text-left flex items-start justify-between gap-3 group"
      >
        <div className="flex items-start gap-2.5 min-w-0">
          <TierIcon weight="duotone" className="text-emerald-800 mt-0.5 shrink-0" size={18} />
          <div className="min-w-0">
            <div className="font-display text-sm font-bold text-slate-950 truncate tracking-tight">
              {agent.name}
            </div>
            <div className="text-[12px] text-slate-700 font-medium leading-snug mt-0.5">
              {agent.summary}
            </div>
          </div>
        </div>
        <span className="font-mono text-[9px] text-emerald-900 tracking-mono-label font-bold shrink-0
                          opacity-70 group-hover:opacity-100 transition-opacity">
          {agent.tier}
        </span>
      </button>

      {/* Confidence weight slider */}
      <div className="mt-3 border-t border-[#E5E7EB] pt-3">
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <span className="font-mono text-[10px] text-slate-700 font-bold tracking-mono-label">
            Confidence Weight
          </span>
          <span className="font-mono text-[10px] text-slate-950 font-bold tabular-nums">
            {weight.toFixed(2)}× · {weightLabel}
          </span>
        </div>
        <input
          type="range"
          min={0.4}
          max={1.6}
          step={0.05}
          value={weight}
          onChange={(e) => onWeight(Number(e.target.value))}
          data-testid={`agent-weight-${agent.id}`}
          className="w-full accent-emerald-800 cursor-pointer"
          aria-label={`${agent.name} confidence weight`}
        />
        <div className="font-mono text-[9px] text-slate-600 font-medium mt-1 tabular-nums">
          {weight < 0.7
            ? "down-weight · PDI scoring softens this agent's signal"
            : weight > 1.3
              ? "up-weight · PDI scoring amplifies this agent's signal"
              : "neutral · standard PDI calibration"}
        </div>
      </div>

      {/* Manual command inlet · Command Partner Lead */}
      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
        className="mt-3 border-t border-[#E5E7EB] pt-3"
      >
        <div className="font-mono text-[10px] text-slate-700 font-bold tracking-mono-label mb-1.5">
          Command Partner Lead
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={directive}
            onChange={(e) => onDirective(e.target.value)}
            placeholder="Issue a tactical directive…"
            data-testid={`agent-command-input-${agent.id}`}
            className="flex-1 bg-[#F9FAFB] border border-[#D1D5DB] focus:border-emerald-800 outline-none
                       rounded-sm px-2.5 py-1.5 text-[12px] text-slate-950 font-medium placeholder:text-slate-500
                       placeholder:italic"
          />
          <button
            type="submit"
            data-testid={`agent-command-submit-${agent.id}`}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm bg-emerald-800 hover:bg-emerald-900
                       text-white font-mono text-[10px] tracking-mono-label font-bold transition-colors"
            title="Dispatch directive"
          >
            <PaperPlaneTilt size={11} weight="bold" /> Dispatch
          </button>
        </div>
      </form>

      {/* Sensor strip */}
      <div className="mt-3 flex items-center gap-1.5 flex-wrap">
        <span className="font-mono text-[9px] text-slate-600 font-bold tracking-mono-label">SENSORS</span>
        {dossier.sensors.map((s, i) => {
          const Icon = s.icon;
          return (
            <span
              key={i}
              title={s.name}
              data-testid={`agent-sensor-${agent.id}-${i}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-[#F3F4F6]
                         border border-[#D1D5DB] text-slate-800 font-mono text-[9px] font-bold"
            >
              <Icon size={10} weight="duotone" /> {s.name}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function AgentDossierDrawer({ agent, weight, onClose }) {
  if (!agent) return null;
  const dossier = AGENT_DOSSIER[agent.tier] || AGENT_DOSSIER.SYSTEM;
  // Pull a deterministic slice of the synthetic decisions for this agent
  const startIdx = Math.abs(hashStr(agent.id)) % PSEUDO_DECISIONS.length;
  const recent = Array.from({ length: 10 }, (_, i) => {
    const text = PSEUDO_DECISIONS[(startIdx + i) % PSEUDO_DECISIONS.length];
    const minutesAgo = (i + 1) * 7;
    return { text, when: `${minutesAgo}m ago` };
  });
  const TierIcon = TIER_ICON[agent.tier] || Robot;

  return (
    <div
      data-testid="agent-dossier-drawer"
      className="fixed inset-0 z-50 flex justify-end"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm" />
      <aside
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-full max-w-xl h-full bg-[#F9FAFB] border-l border-[#D1D5DB] flex flex-col"
      >
        <header className="px-5 py-4 border-b border-[#D1D5DB] bg-[#F3F4F6] flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display text-base font-bold text-slate-950 flex items-center gap-2 tracking-tight">
              <TierIcon weight="duotone" className="text-emerald-800" size={20} />
              {agent.name}
            </h3>
            <div className="text-[12px] text-slate-800 font-medium mt-0.5 leading-snug">
              {agent.summary}
            </div>
            <div className="mt-1.5 flex items-center gap-2 font-mono text-[10px] text-slate-700 font-bold flex-wrap">
              <span className="px-1.5 py-0.5 rounded-sm bg-white border border-[#D1D5DB]">{agent.tier}</span>
              <span>· weight {weight.toFixed(2)}×</span>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="agent-dossier-close"
            className="p-1 rounded-sm text-slate-700 hover:text-slate-950 hover:bg-[#E5E7EB]"
          >
            <X size={16} weight="bold" />
          </button>
        </header>

        <div className="overflow-auto thin-scroll flex-1 p-5 space-y-4 bg-white">
          {/* Active Pursuit */}
          <section data-testid="dossier-active-pursuit">
            <div className="flex items-center gap-2 mb-2">
              <Crosshair weight="duotone" className="text-emerald-800" size={16} />
              <span className="font-display text-sm font-bold text-slate-950 tracking-tight">
                Active Pursuit
              </span>
            </div>
            <ul className="space-y-1 text-sm text-slate-950 font-medium">
              {dossier.pursuits.map((p, i) => (
                <li key={i} className="flex gap-2 leading-relaxed">
                  <span className="text-emerald-800 font-bold">▸</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <p className="text-[12px] text-slate-700 font-mono font-medium mt-2 leading-snug">
              {dossier.decisionStyle}
            </p>
          </section>

          {/* Sensors */}
          <section data-testid="dossier-sensors">
            <div className="flex items-center gap-2 mb-2">
              <Broadcast weight="duotone" className="text-emerald-800" size={16} />
              <span className="font-display text-sm font-bold text-slate-950 tracking-tight">
                Sensors · Live Data Feeds
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {dossier.sensors.map((s, i) => {
                const Icon = s.icon;
                return (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-[#F3F4F6]
                               border border-[#D1D5DB] text-slate-950 font-mono text-[11px] font-bold"
                  >
                    <Icon size={12} weight="duotone" /> {s.name}
                  </span>
                );
              })}
            </div>
          </section>

          {/* Decision Log */}
          <section data-testid="dossier-decision-log">
            <div className="flex items-center gap-2 mb-2">
              <Pulse weight="duotone" className="text-emerald-800" size={16} />
              <span className="font-display text-sm font-bold text-slate-950 tracking-tight">
                Decision Log · last 10 PDI signals
              </span>
            </div>
            <ol className="space-y-1.5">
              {recent.map((d, i) => (
                <li
                  key={i}
                  className="grid grid-cols-12 gap-2 text-[12.5px] text-slate-950 font-medium border-l-2 border-emerald-700/50 pl-3"
                >
                  <span className="col-span-2 font-mono text-[10px] text-slate-700 tracking-mono-label font-bold tabular-nums">
                    {d.when}
                  </span>
                  <span className="col-span-10 leading-relaxed">{d.text}</span>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </aside>
    </div>
  );
}

function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}

// ─── Methods & Tools ─────────────────────────────────────────────────────────
function MethodsView() {
  const verbs = [
    { name: "Run Should-Cost",   pattern: "should-cost PO-XXXXX-XXX",      tier: "SME → ORCHESTRATOR" },
    { name: "Run Rate Benchmark", pattern: "rate benchmark PO-XXXXX-XXX",   tier: "SME → ORCHESTRATOR" },
    { name: "Show Risk for Supplier", pattern: "risk for [supplier name]",  tier: "SME → ORCHESTRATOR" },
    { name: "Find PO + open Proof",   pattern: "find PO-XXXXX-XXX",         tier: "UTILITY → SME → ORCHESTRATOR" },
    { name: "Show Funnel Stage",      pattern: "observe | orient | decide | act", tier: "navigation" },
  ];
  return (
    <Wrapper title="Methods & Tools" icon={Toolbox} subtitle="The verb registry exposed by the Command Palette. Each verb routes through the tiered pipeline.">
      <ul className="divide-y divide-slate-800">
        {verbs.map((v, i) => (
          <li key={i} className="px-5 py-3" data-testid={`method-${i}`}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-slate-100 text-sm">{v.name}</span>
              <span className="font-mono text-[10px] text-fuchsia-300">{v.tier}</span>
            </div>
            <code className="font-mono text-[11px] text-emerald-300">{v.pattern}</code>
          </li>
        ))}
      </ul>
    </Wrapper>
  );
}

// ─── Scenario Simulator — superseded by StrategyMultiplierView (sidebar id: "multiplier") ─

// ─── Market Boards (FX, Commodities, Signals, Geopolitical) ─────────────────
const SYNTHETIC_MARKET = {
  fx: [
    ["EUR/USD",  1.084, "+0.12%"], ["USD/JPY", 154.21, "−0.34%"], ["GBP/USD", 1.273, "+0.08%"],
    ["DXY (Dollar Index)", 104.62, "+0.21%"], ["USD/CNY", 7.252, "−0.05%"],
  ],
  commodities: [
    ["FRED DCOILWTICO · WTI", "$78.04 / bbl", "−1.4% MoM"],
    ["FRED PCOPPUSDM · Copper", "$9,420 / mt", "+3.2% MoM"],
    ["BLS WPU114 · Construction Mfg", "267.5", "+0.4% YoY"],
    ["BLS WPU101 · Iron & Steel", "312.1", "−0.8% MoM"],
    ["FRED PPIACO · All Commodities", "258.2", "flat"],
  ],
  signals: [
    ["DCAA forward-pricing rate agreement (FPRA) renewals", "12 pending across DOD primes", "amber"],
    ["SBA Small-Biz set-aside fill rate", "63% YTD vs 23% goal", "emerald"],
    ["Section 889 part B compliance risk", "3 vendors flagged", "rose"],
    ["GSA Schedule 70 IT services price index", "+2.3% YoY", "amber"],
  ],
  geo: [
    ["China rare-earth export controls", "Tightening Q2", "rose"],
    ["Red Sea shipping diversion", "Suez throughput −34% YoY", "rose"],
    ["EU CBAM (carbon border)", "Reporting live; payment live 2026", "amber"],
    ["US CHIPS Act tier-2 awards", "$3.4B disbursed last quarter", "emerald"],
  ],
};

function MarketBoard({ title, icon, kind }) {
  const rows = SYNTHETIC_MARKET[kind] || [];
  return (
    <Wrapper title={title} icon={icon} subtitle="Synthetic market feed · cite source codes (FRED, BLS, GSA) for procurement context. Live connectors planned in the Collectors pane.">
      <ul className="divide-y divide-slate-800" data-testid={`market-${kind}`}>
        {rows.map((r, i) => (
          <li key={i} className="px-5 py-3 flex items-center gap-4">
            <span className="flex-1 text-slate-200 text-sm">{r[0]}</span>
            <span className="font-mono text-emerald-300 text-sm">{r[1]}</span>
            <span className={`font-mono text-[11px] ${r[2]?.toString().startsWith("−") || r[2]?.toString().startsWith("-") || r[2] === "rose" ? "text-rose-300" : r[2] === "amber" ? "text-amber-300" : "text-emerald-300"}`}>
              {typeof r[2] === "string" && ["rose","amber","emerald"].includes(r[2]) ? "▸ flag" : r[2]}
            </span>
          </li>
        ))}
      </ul>
    </Wrapper>
  );
}

// ─── Collectors (data sources) ───────────────────────────────────────────────
function CollectorsView() {
  const [c, setC] = useState(null);
  const [p, setP] = useState(null);
  useEffect(() => {
    api.soc2().then(setC).catch(() => setC({ gitleaks_history: [] }));
    api.roadmapPhase2().then(setP).catch(() => setP({ criteria: [] }));
  }, []);
  if (!c || !p) return <Skeleton />;
  const importer = p.criteria.find((x) => x.id === "P2-01");
  const samFeed  = p.criteria.find((x) => x.id === "P2-04");
  const sources = [
    { name: "Enterprise Awards Feed",  detail: samFeed?.metric || "—",          status: samFeed?.status || "PENDING" },
    { name: "Spend Importer · SAP / Coupa", detail: importer?.metric || "—",         status: importer?.status || "PENDING" },
    { name: "SEC EDGAR 10-K extractor",    detail: "On-demand · via SME tier",     status: "READY" },
    { name: "FRED · PCOPPUSDM, DCOILWTICO, PPIACO", detail: "Synthetic feed · live connector P3", status: "STUB" },
    { name: "BLS · PPI/ECI series",         detail: "Synthetic feed · live connector P3",       status: "STUB" },
    { name: "Gitleaks scanner",             detail: `${c.gitleaks_history.length}-day rolling history · 0 active`, status: "READY" },
  ];
  return (
    <Wrapper title="Collectors" icon={HardDrives} subtitle="External data ingestion sources and their connector health.">
      <ul className="divide-y divide-slate-800">
        {sources.map((s, i) => (
          <li key={i} className="px-5 py-3 flex items-center gap-4">
            <span className="flex-1 min-w-0">
              <div className="text-slate-100 text-sm truncate">{s.name}</div>
              <div className="text-[11px] text-slate-400 font-mono">{s.detail}</div>
            </span>
            <span className={`font-mono text-[10px] tracking-mono-label px-2 py-0.5 rounded-sm border ${
              s.status === "READY" || s.status === "PASS"
                ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
                : s.status === "STUB" || s.status === "PENDING"
                ? "text-slate-300 border-slate-500/40 bg-slate-500/10"
                : "text-sky-300 border-sky-500/40 bg-sky-500/10"
            }`}>{s.status}</span>
          </li>
        ))}
      </ul>
    </Wrapper>
  );
}

// ─── System Health ───────────────────────────────────────────────────────────
function SystemHealthView() {
  const [a, setA] = useState(null);
  const [c, setC] = useState(null);
  const [r, setR] = useState(null);
  useEffect(() => {
    api.integrity().then(setA).catch(() => setA(null));
    api.soc2().then(setC).catch(() => setC(null));
    api.roadmapPhase2().then(setR).catch(() => setR(null));
  }, []);
  if (!a || !c || !r) return <Skeleton />;
  return (
    <Wrapper title="System" icon={ShieldCheckered} subtitle="Top-line health of the headless engine. Integrity · SOC 2 · Phase-2 exit readiness.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-800">
        <Card title="Integrity Assertions">
          <li className="px-5 py-3">
            <div className="font-mono text-2xl text-emerald-300">{a.passed}/{a.total}</div>
            <div className="text-[11px] text-slate-400">SQL assertions passing</div>
          </li>
        </Card>
        <Card title="SOC 2 Controls">
          <li className="px-5 py-3">
            <div className="font-mono text-2xl text-emerald-300">{c.summary.pass}/{c.summary.total}</div>
            <div className="text-[11px] text-slate-400">{c.summary.warn} warn · {c.summary.fail} fail</div>
          </li>
        </Card>
        <Card title="Phase 2 Exit Readiness">
          <li className="px-5 py-3">
            <div className={`font-mono text-2xl ${r.verified ? "text-emerald-300" : "text-amber-300"}`}>{r.passed}/{r.total}</div>
            <div className="text-[11px] text-slate-400">{r.verified ? "EXIT READY" : "IN FLIGHT"}</div>
          </li>
        </Card>
      </div>
    </Wrapper>
  );
}

// ─── Shared chrome ───────────────────────────────────────────────────────────
function Wrapper({ title, icon, subtitle, children }) {
  const Icon = icon;
  return (
    <section className="bg-slate-900 border border-slate-800">
      <header className="px-5 py-3 border-b border-slate-800">
        <h2 className="font-display text-base font-semibold text-slate-50 flex items-center gap-2">
          <Icon weight="duotone" className="text-emerald-400" size={16} />
          {title}
        </h2>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5 font-mono">{subtitle}</p>}
      </header>
      {children}
    </section>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-slate-950">
      <div className="px-3 py-2 border-b border-slate-800 tracking-mono-label text-[10px] text-slate-500">{title}</div>
      <ul className="divide-y divide-slate-800">{children}</ul>
    </div>
  );
}

function Empty({ text }) {
  return <div className="px-5 py-10 text-center text-xs font-mono text-slate-500">{text}</div>;
}

function Skeleton() {
  return <div className="h-[260px] skeleton rounded-sm" />;
}

export const SECTION_META = ALL_SECTIONS;
